import { useState, useEffect, useContext } from 'react'
import { useFormik } from 'formik'
import {
  getChoices,
  getFormInitValues,
  dynamicValidation,
  deboundRequest
} from '../../services/utils'
import {
  FormFieldProps,
  FormsProps,
  SelectOption
} from '../../services/api/types'
import { submitForm, submitFormWithFiles } from '../../services/api/cms'
import { EnquiryForm, EnquiryFormRow } from './MemberEnquiryForm.styled'
import * as F from './Form.styled'
import * as G from '../../styles/global.styles'
import { Button } from '../base/Button'
import { CareerFormHeader } from './CareerForm.styled'
import {
  TextAreaInput,
  DropdownStyled,
  TextInput,
  CheckBox,
  Radio,
  BlockTitle,
  GymsSelect,
  FileInput,
  DatePickerStyled
} from '../base'
import useGymOptions from '../../hooks/useGymOptions'
import { RECAPTCHA_KEY } from '../../services/api/constants'
import { loadReCaptcha, ReCaptcha } from 'react-recaptcha-v3'
import { getCookieFromBrowser } from '../../services/api/cookies'
import PrivacyField from './PrivacyField'
import PhoneInput, { CountryData } from 'react-phone-input-2'
import { SiteContext } from '../../services/context/SiteContext'
import { getSiteCode } from '../../services/api/helpers'

export interface CareerFormProps extends FormsProps {
  formHeader?: string
  submitText: string
  onSubmit?: Function
}

export const CareerForm = (props: CareerFormProps) => {
  const { formHeader, slug, fields, submitText } = props
  const [success, setSuccess] = useState(false)
  const [responseMessage, setResponseMessage] = useState('')
  const hasRecaptcha =
    fields &&
    fields.length > 0 &&
    fields?.find(it => it.field_block === 'recaptcha')
      ? true
      : false
  const [recaptchaDisableSubmit, setRecaptchaDisableSubmit] = useState(
    hasRecaptcha
  )
  let fileFieldText: Array<string> = []
  fields &&
    fields.length > 0 &&
    fields.map(field => {
      if (field.type === 'file') {
        fileFieldText.push(field.name)
      }
    })
  const hasFile = fileFieldText && fileFieldText.length > 0

  const formik = useFormik({
    initialValues: getFormInitValues(props.fields),
    validationSchema: dynamicValidation(props.fields),
    onSubmit: async values => {
      let fileData: any
      let fieldData: any
      if (hasFile) {
        fileData = Object.keys(values)
          .filter(key => fileFieldText.includes(key))
          .reduce((obj: any, key: string) => {
            obj[key] = values[key]
            return obj
          }, {})
        fieldData = Object.keys(values)
          .filter((key: string) => !fileFieldText.includes(key))
          .reduce((obj: any, key: string) => {
            obj[key] = values[key]
            return obj
          }, {})
      }

      if (!slug) {
        return
      }

      const utmSourceCookie = getCookieFromBrowser('utm_source')
      const utmCampaignCookie = getCookieFromBrowser('utm_campaign')
      if (utmSourceCookie && utmCampaignCookie) {
        values['utm_source'] = utmSourceCookie
        values['utm_campaign'] = utmCampaignCookie
      }

      let res = hasFile
        ? await submitFormWithFiles(null, slug, fieldData, fileData)
        : await submitForm(null, slug, values)
      if (res.ok) {
        setResponseMessage(res.success_message)
        setSuccess(true)
      } else {
        setResponseMessage(res.error_message)
        setSuccess(false)
        throw responseMessage
      }
    }
  })
  const textAreaInput = (field: FormFieldProps, isInvalid?: boolean) => {
    switch (field.field_block) {
      case 'label':
        return (
          <F.FormGroup col={1}>
            <F.FormLabel>{field.label}</F.FormLabel>
          </F.FormGroup>
        )
      case 'heading':
        return <BlockTitle textTransform='uppercase'>{field.label}</BlockTitle>
      case 'multiline':
        return (
          <TextAreaInput
            rows={4}
            cols={28}
            name={field.name}
            borderColor={isInvalid ? 'red' : undefined}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        )
    }
  }
  const {
    gymOptions,
    isLoadingGyms,
    setChoosenState,
    setChoosenPostCode
  } = useGymOptions('', '', fields, props.slug)

  const { siteInfor } = useContext(SiteContext)
  const siteCode = getSiteCode(siteInfor?.root_url)

  useEffect(() => {
    if (!window.RECAPTCHA_INITIALIZED && hasRecaptcha) {
      loadReCaptcha(RECAPTCHA_KEY)
      window.RECAPTCHA_INITIALIZED = true
    }
  }, [hasRecaptcha])

  return (
    <F.FormWrapper>
      <EnquiryForm onSubmit={formik.handleSubmit}>
        {success ? (
          <>
            <CareerFormHeader>{'Your enquiry has been sent.'}</CareerFormHeader>
            <G.Space height={1} />
          </>
        ) : (
          <CareerFormHeader>{formHeader}</CareerFormHeader>
        )}

        {!success &&
          fields?.map((field, idx) => {
            let inputComp
            const isInvalid =
              formik && formik.errors[field.name] && formik.touched[field.name]
            switch (field.type) {
              case 'text_area': {
                inputComp = textAreaInput(field, !!isInvalid)
                break
              }
              case 'select': {
                const selectOptions = getChoices(field.choices)
                if (field.field_block == 'gym') {
                  inputComp = (
                    <GymsSelect
                      placeholder={field.help_text || field.label}
                      name={field.name}
                      defaultOptions={gymOptions}
                      onChange={(option: SelectOption) =>
                        formik.setFieldValue(field.name, option.value)
                      }
                      onBlur={formik.handleBlur}
                      isDisabled={isLoadingGyms}
                      isLoading={isLoadingGyms}
                      formSlug={props.slug}
                    />
                  )
                } else {
                  inputComp = (
                    <DropdownStyled
                      placeholder={field.help_text || field.label}
                      name={field.name}
                      type={field.type}
                      options={selectOptions}
                      onChange={(option: SelectOption) => {
                        if (field.name === 'state') {
                          setChoosenState(option.value)
                        }
                        formik?.setFieldValue(field.name, option.value)
                      }}
                      onBlur={formik?.handleBlur}
                    />
                  )
                }
                break
              }
              case 'checkbox': {
                const selectOptions = getChoices(field.choices)
                if (field.field_block === 'multi_select') {
                  inputComp = (
                    <DropdownStyled
                      placeholder={field.help_text || field.label}
                      name={field.name}
                      type={field.type}
                      options={selectOptions}
                      isMulti
                      onChange={(options: SelectOption[]) => {
                        if (field.name === 'state') {
                          setChoosenState(
                            options?.map(it => it.value)?.toString() ?? ''
                          )
                        }
                        formik.setFieldValue(
                          field.name,
                          options?.map(it => it.value) ?? ''
                        )
                      }}
                      onBlur={formik?.handleBlur}
                    />
                  )
                } else {
                  inputComp = (
                    <>
                      {selectOptions.map(op => {
                        return (
                          <CheckBox
                            name={field.name}
                            type={field.type}
                            value={op.value}
                            label={op.label}
                            color='black'
                            borderColor={isInvalid ? 'red' : 'white'}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        )
                      })}
                    </>
                  )
                }

                break
              }
              case 'radio': {
                const selectOptions = getChoices(field.choices)
                inputComp = (
                  <>
                    {selectOptions.map(op => {
                      return (
                        <Radio
                          name={field.name}
                          type={field.type}
                          value={op.value}
                          label={op.label}
                          color='black'
                          borderColor={isInvalid ? 'red' : 'white'}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      )
                    })}
                  </>
                )
                break
              }
              case 'file': {
                inputComp = (
                  <FileInput
                    name={field.name}
                    type={field.type}
                    text={field.label}
                    color='black'
                    borderColor={isInvalid ? 'red' : 'white'}
                    onChange={(event: any) =>
                      formik?.setFieldValue(
                        field.name,
                        event.currentTarget.files[0]
                      )
                    }
                    onBlur={formik.handleBlur}
                  />
                )
                break
              }
              case 'input': {
                if (field.field_block == 'recaptcha') {
                  inputComp = (
                    <ReCaptcha
                      sitekey={RECAPTCHA_KEY}
                      action='careerForm'
                      verifyCallback={async (token: any) => {
                        if (formik.values['recaptcha'] !== token)
                          await formik.setFieldValue('recaptcha', token, false)
                        setRecaptchaDisableSubmit(false)
                      }}
                    />
                  )
                } else {
                  inputComp = (
                    <TextInput
                      placeholder={field.help_text}
                      name={field.name}
                      type={field.type}
                      color='black'
                      borderColor={isInvalid ? 'red' : 'white'}
                      onChange={(e: any) => {
                        if (field.name === 'postcode') {
                          deboundRequest(setChoosenPostCode, e.target.value)
                        }
                        formik.setFieldValue(field.name, e.target.value)
                      }}
                      onBlur={formik.handleBlur}
                    />
                  )
                }
                break
              }
              case 'text': {
                if (field.field_block == 'date') {
                  inputComp = (
                    <DatePickerStyled
                      name={field.name}
                      setFieldValue={formik?.setFieldValue}
                      showYearDropdown
                    />
                  )
                } else if (field.field_block == 'datetime') {
                  inputComp = (
                    <DatePickerStyled
                      timePicker={true}
                      name={field.name}
                      setFieldValue={formik?.setFieldValue}
                      showYearDropdown
                    />
                  )
                } else if (field.field_block == 'phone_number') {
                  inputComp = (
                    <PhoneInput
                      country={siteCode}
                      enableSearch
                      countryCodeEditable={false}
                      placeholder={field.help_text}
                      value={formik.values[field.name]}
                      onChange={(value: any, data: CountryData) => {
                        formik.setFieldValue(field.name, `+${value}`)
                        formik.setFieldValue(
                          'country-code',
                          `+${data.dialCode}`
                        )
                      }}
                      onBlur={formik.handleBlur}
                    />
                  )
                } else {
                  inputComp = (
                    <TextInput
                      placeholder={field.help_text}
                      name={field.name}
                      type={field.type}
                      color='black'
                      borderColor={isInvalid ? 'red' : 'white'}
                      onChange={(e: any) => {
                        if (field.name === 'postcode') {
                          deboundRequest(setChoosenPostCode, e.target.value)
                        }
                        formik.setFieldValue(field.name, e.target.value)
                      }}
                      onBlur={formik.handleBlur}
                    />
                  )
                }
                break
              }
              default:
                inputComp = (
                  <TextInput
                    placeholder={field.help_text || field.label}
                    name={field.name}
                    type={field.type}
                    color='black'
                    borderColor={isInvalid ? 'red' : 'white'}
                    onChange={(e: any) => {
                      if (field.name === 'postcode') {
                        deboundRequest(setChoosenPostCode, e.target.value)
                      }
                      formik.setFieldValue(field.name, e.target.value)
                    }}
                    onBlur={formik.handleBlur}
                  />
                )
                break
            }

            if (field.field_block === 'privacypolicy') {
              inputComp = <PrivacyField field={field} formik={formik} />
            }

            return (
              <F.FormGroup key={idx}>
                {inputComp}
                <F.ErrorMessage>
                  {formik.errors[field.name] &&
                    formik.touched[field.name] &&
                    formik.errors[field.name]}
                </F.ErrorMessage>
              </F.FormGroup>
            )
          })}
        {formik.isSubmitting ? (
          <G.SpinnerWrap>
            <G.Spinner />
          </G.SpinnerWrap>
        ) : !success ? (
          <EnquiryFormRow justify='center'>
            <Button
              type='submit'
              color='blue'
              disabled={formik.isSubmitting || recaptchaDisableSubmit}
            >
              {submitText || 'SUBMIT'}
            </Button>
            {hasRecaptcha && (
              <ReCaptcha
                sitekey={RECAPTCHA_KEY}
                action='careerForm'
                verifyCallback={async (token: any) => {
                  if (formik.values['recaptcha'] !== token)
                    await formik.setFieldValue('recaptcha', token, false)
                  setRecaptchaDisableSubmit(false)
                }}
              />
            )}
          </EnquiryFormRow>
        ) : null}
      </EnquiryForm>
    </F.FormWrapper>
  )
}

export default CareerForm
